/**
 * Weekly / higher-timeframe plumbing for the CUE shared indicators
 * (Weekly Bongo, Weekly RSMA).
 *
 * This module IS the single source of week/month boundaries — `bucketKey` below.
 * `customBongoHtf` and the Pine runtime's `request.security` both import from
 * here rather than keeping their own copy, which is the point: two parts of the
 * app disagreeing about when a week starts is a bug nobody can see.
 *
 * Two projections live here, and they are not interchangeable:
 *   - `lastClosedHtIndexPerDay` — Pine's `lookahead_off`. A period is readable
 *     on the bar it CLOSES on, and the newest bar reads the forming one.
 *   - `htIndexPerDay` — the period each bar BELONGS to, in progress included.
 *     Used where a study should read the live current week.
 * Which one a study wants is a product decision (design principle 9); picking
 * the wrong one is invisible until someone diffs against TradingView.
 *
 * These operate on index-aligned `times`/`closes` arrays (from `OHLCV`), not
 * `Bar[]`, so they live in the framework-agnostic shared layer. If `times` is
 * absent the caller returns all-nulls.
 */

export type HtFreq = "day" | "week" | "month";

/** US-session day cut: 06:00 UTC = 01:00 EST / 02:00 EDT, inside the
 *  20:00→04:00 ET overnight dead zone in BOTH daylight regimes. Bucketing
 *  intraday bars by UTC calendar date would split a session at 19:00/20:00
 *  ET (mid post-market); shifting the cut into the dead zone keeps every
 *  pre-market→post-market bar of one ET session in one bucket without
 *  dragging timezone tables into this shared layer. Daily+ bars (stamped at
 *  UTC midnight) shift uniformly, so their keys stay distinct per bar. */
const DAY_CUT_MS = 6 * 3_600_000;

/**
 * The SESSION a bar belongs to, as that session's UTC-midnight timestamp.
 *
 * Two stamping conventions meet here and they need different treatment:
 *
 *   DAILY AND SLOWER bars are stamped at UTC midnight OF the session date
 *   (`fetchFmpBars` does `Date.parse(date + "T00:00:00Z")`). The timestamp
 *   already IS the session date, so it is returned unchanged. Subtracting the
 *   day cut from one of these would move it to the PREVIOUS day, which is
 *   harmless for a day ordinal (a uniform shift keeps every bar distinct) and
 *   quietly wrong for a month: 2026-05-01 would bucket as April.
 *
 *   INTRADAY bars are stamped at their real instant, so an ET session runs
 *   from 04:00 ET to 20:00 ET and its tail crosses UTC midnight. The 6h cut
 *   pulls that tail back into the session it belongs to.
 *
 * This is why `bucketKey` can no longer read `new Date(t)` directly for week
 * and month: it did, so on an INTRADAY chart every bar in the 20:00-24:00 ET
 * stretch landed in the next day's week — and, one day a month, the next
 * MONTH. Daily charts never saw it because their timestamps are already
 * session dates.
 *
 * ## `intraday` is a property of the SERIES, not of the timestamp
 *
 * The first version of this inferred the grain per bar, from
 * `t % 86_400_000 === 0`. That is wrong at exactly the point it matters: a
 * 5-minute bar at 20:00 EDT IS 00:00 UTC, so the last bar of the session — the
 * one the cut exists for — was the single bar treated as daily, and a June 30
 * session came out spread across three monthly buckets. `pine:check` 6d caught
 * it. The grain is knowable from the series and from nothing else, so callers
 * pass it (see `isIntradaySeries`) and every bar of one series is treated
 * alike.
 */
export function sessionDayUtc(t: number, intraday: boolean): number {
  if (!intraday) return Math.floor(t / 86_400_000) * 86_400_000;
  return Math.floor((t - DAY_CUT_MS) / 86_400_000) * 86_400_000;
}

/**
 * Is this a series of intraday bars?
 *
 * A daily-or-slower series has EVERY bar stamped at UTC midnight; an intraday
 * one does not. One counter-example settles it, and a 5-minute session
 * produces 78 of them — but the question is asked of the whole array precisely
 * so that the 00:00 UTC bar cannot answer it alone.
 */
export function isIntradaySeries(times: ReadonlyArray<number>): boolean {
  for (let i = 0; i < times.length; i++) {
    if (times[i]! % 86_400_000 !== 0) return true;
  }
  return false;
}

/** ISO-week / calendar-month / session-day bucket key for a bar timestamp
 *  (ms, UTC). Week: Monday-start ISO week (e.g. "2026-W21"). Month:
 *  "YYYY-MM". Day: session-day ordinal.
 *
 *  `intraday` defaults to false — the daily convention, which is what every
 *  caller holding a stored daily series wants. A caller holding an intraday
 *  series must pass `isIntradaySeries(times)`; the resamplers below all do. */
export function bucketKey(t: number, freq: HtFreq, intraday = false): string {
  const session = sessionDayUtc(t, intraday);
  if (freq === "day") {
    return `D${session / 86_400_000}`;
  }
  // Memoized on the SESSION DAY, which is the only thing the answer depends on.
  //
  // `sessionDayUtc` above reduces any timestamp — daily or intraday — to a
  // UTC-midnight session day, and everything below reads only that. So for a
  // given (session day, freq) the key is a pure function, and `intraday` has
  // no further influence once the reduction has happened. That makes this an
  // exact cache, not an approximation.
  //
  // Worth caching because of the call volume, which is not obvious from here.
  // A single panel row resamples the SAME ~760-bar window about twenty times:
  // person (week + month), cpr (x3), hlcd (x2), force (once per weekly
  // variant, six of them), rs (x2), obv, bongo (x2), ems (x4), lcc, pps,
  // jpHistogram — each family resampling independently. That is ~15,000
  // `bucketKey` calls per symbol, each allocating up to four `Date` objects
  // and building a template string. Profiled 2026-09-15 over 224 real symbols:
  // **901 ms of 2,466 ms of panel compute, 37% of the bake, in this one
  // function** — more than any indicator in it.
  //
  // The key space is bounded by real calendar days (the session calendar holds
  // 16,351), so this cannot grow without bound even when intraday callers feed
  // it per-minute timestamps: they reduce to session days too.
  const cache = freq === "month" ? monthKeyBySessionDay : weekKeyBySessionDay;
  const cached = cache.get(session);
  if (cached !== undefined) return cached;
  const computed = computeBucketKey(session, freq);
  cache.set(session, computed);
  return computed;
}

const weekKeyBySessionDay = new Map<number, string>();
const monthKeyBySessionDay = new Map<number, string>();

/** The uncached arithmetic, kept verbatim so the cache cannot change an answer. */
function computeBucketKey(session: number, freq: Exclude<HtFreq, "day">): string {
  const d = new Date(session);
  if (freq === "month") {
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
  }
  // ISO week: shift to the Thursday of the same week, then number from the
  // year's first Thursday (the standard ISO-8601 algorithm).
  const tmp = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const day = tmp.getUTCDay() || 7; // Mon=1 … Sun=7
  tmp.setUTCDate(tmp.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((tmp.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `${tmp.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}

/**
 * A higher timeframe as TradingView writes it: a base period and a multiplier.
 * "3M" is three months, "2W" two weeks, "D" one day.
 */
export type HtTf = { freq: HtFreq; mult: number };

/** Monday of the ISO week containing session-day `sessionUtc` (UTC midnight). */
function mondayUtcOf(sessionUtc: number): number {
  const d = new Date(sessionUtc);
  const dow = d.getUTCDay() || 7; // Mon=1 … Sun=7
  return sessionUtc - (dow - 1) * 86_400_000;
}

/** 1970-01-05 was a Monday — the anchor for absolute 1W numbering. */
const EPOCH_MONDAY = Date.UTC(1970, 0, 5);

/** First Monday of a UTC calendar year (Jan 1 when that day is Monday). */
function firstMondayOfYear(year: number): number {
  const jan1 = Date.UTC(year, 0, 1);
  const dow = new Date(jan1).getUTCDay() || 7;
  return jan1 + ((8 - dow) % 7) * 86_400_000;
}

/**
 * Start Monday of the n-week block containing `sessionUtc` (UTC midnight).
 * TradingView's `time("2W")` is 09:30 ET of this Monday even when it is a
 * holiday and the first traded session is Tuesday.
 */
export function htWeekBlockMonday(sessionUtc: number, n: number): number {
  const step = Math.max(1, Math.floor(n)) * 7 * 86_400_000;
  const monday = mondayUtcOf(sessionUtc);
  const sessionYear = new Date(sessionUtc).getUTCFullYear();
  let year = sessionYear;
  let firstMon = firstMondayOfYear(year);
  if (monday < firstMon) {
    year -= 1;
    firstMon = firstMondayOfYear(year);
  }
  const k = Math.floor((monday - firstMon) / step);
  return firstMon + k * step;
}

/**
 * Group ids for a bar series under a multiplied timeframe. Bars sharing an id
 * belong to the same higher-timeframe bar.
 *
 * ## Why this exists at all
 *
 * `request.security` used to send anything that was not exactly D/1D, W/1W or
 * M/1M to a FIXED-WIDTH, EPOCH-ANCHORED clock resampler. "3M" became
 * `3 * 43,200 minutes` = a flat 90-day block measured from 1970-01-01, so on a
 * 2026 chart the quarters ran 2026-03-09..2026-06-05 and 2026-06-08..2026-09-03
 * — not calendar quarters, not aligned to anything, and silent. "3D" counted
 * weekends as days. Meanwhile `time("3M")` grouped real calendar months, so
 * the two disagreed with each other: a script gating on
 * `time("3M") != time("3M")[1]` saw a new quarter on 2026-04-01 while the value
 * it read rolled over on 2026-06-05.
 *
 * Both now come through here, so that particular disagreement is not
 * expressible.
 *
 * ## The anchors, and how much each is worth
 *
 * MONTH multiples are year-aligned: `year * ceil(12/n) + floor(month/n)`.
 * Multiples that divide 12 tile the year (`"3M"` is Jan-Mar / Apr-Jun / …).
 * Multiples that do not (`"5M"`) leave a short leftover at year end
 * (Jan-May / Jun-Oct / Nov-Dec) — pinned by the `month-5m` TradingView export.
 *
 * WEEK multiples of 1 are ISO weeks (Monday). Multiples above 1 start on the
 * first Monday of the calendar year and step `n` weeks — pinned by `week-2w`.
 * Days before that Monday stay in the previous year's last block.
 *
 * DAY multiples group SESSIONS (weekends are never counted) from the first
 * session of each calendar year, then step `n` — pinned by `day-3d`. A
 * series that starts mid-year is a leftover block until the next January.
 *
 * For a multiplier of 1 every branch reduces exactly to `bucketKey`'s
 * partition, so the existing weekly/monthly studies are untouched.
 */
export function htGroupIds(times: ReadonlyArray<number>, tf: HtTf): number[] {
  const { freq, mult } = tf;
  const n = Math.max(1, Math.floor(mult));
  const out: number[] = new Array(times.length).fill(0);
  const intraday = isIntradaySeries(times);

  if (freq === "month") {
    const yearStride = Math.ceil(12 / n);
    for (let i = 0; i < times.length; i++) {
      const d = new Date(sessionDayUtc(times[i]!, intraday));
      out[i] = d.getUTCFullYear() * yearStride + Math.floor(d.getUTCMonth() / n);
    }
    return out;
  }

  if (freq === "week") {
    for (let i = 0; i < times.length; i++) {
      const session = sessionDayUtc(times[i]!, intraday);
      if (n === 1) {
        out[i] = Math.round((mondayUtcOf(session) - EPOCH_MONDAY) / (7 * 86_400_000));
        continue;
      }
      out[i] = htWeekBlockMonday(session, n);
    }
    return out;
  }

  // Day: group SESSIONS, not calendar days. TradingView resets the count
  // on the first session of each calendar year (`day-3d`).
  let year = -1;
  let ordinalInYear = -1;
  let prev: number | null = null;
  for (let i = 0; i < times.length; i++) {
    const session = sessionDayUtc(times[i]!, intraday);
    const y = new Date(session).getUTCFullYear();
    if (y !== year) {
      year = y;
      ordinalInYear = 0;
      prev = session;
    } else if (session !== prev) {
      ordinalInYear++;
      prev = session;
    }
    out[i] = year * 1000 + Math.floor(ordinalInYear / n);
  }
  return out;
}

/**
 * Does this timeframe's anchor match TradingView's, as far as this repo can
 * establish? Returns null when it does, or a plain-English reason when it is a
 * choice we made rather than a rule we verified.
 *
 * Deliberately conservative. The alternative — resampling silently and letting
 * the numbers look plausible — is exactly how the 90-day "quarter" survived.
 */
export function htAnchorCaveat(tf: HtTf): string | null {
  // 1-unit D/W/M, year-aligned month multiples, first-Monday 2W+, and
  // year-reset 3D+ are all pinned by pine:check 6c and the TV fixtures.
  void tf;
  return null;
}

/** Resample daily series → higher-timeframe close snapshots: the LAST close +
 *  LAST timestamp of each bucket (what Pine's `request.security` returns once a
 *  period closes). Returns parallel `htTimes`/`htCloses` arrays. */
export function resampleByLastClose(
  times: ReadonlyArray<number>,
  closes: ReadonlyArray<number>,
  freq: HtFreq,
): { htTimes: number[]; htCloses: number[] } {
  const htTimes: number[] = [];
  const htCloses: number[] = [];
  const n = Math.min(times.length, closes.length);
  if (n === 0) return { htTimes, htCloses };
  // The grain is a property of the series — see `isIntradaySeries`. Computed
  // once here so every bar of one series buckets under the same rule.
  const intraday = isIntradaySeries(times);
  let curKey = bucketKey(times[0]!, freq, intraday);
  let curT = times[0]!;
  let curC = closes[0]!;
  for (let i = 1; i < n; i++) {
    const k = bucketKey(times[i]!, freq, intraday);
    if (k === curKey) {
      curT = times[i]!;
      curC = closes[i]!;
    } else {
      htTimes.push(curT);
      htCloses.push(curC);
      curKey = k;
      curT = times[i]!;
      curC = closes[i]!;
    }
  }
  htTimes.push(curT); // trailing (in-progress) bucket; the strict-before filter excludes it
  htCloses.push(curC);
  return { htTimes, htCloses };
}

/** Resample daily OHLC → higher-timeframe bars: open = first bar of the bucket,
 *  high/low = bucket extremes, close = last close, t = last bar's time. Parallel
 *  arrays index-aligned. For indicators that need full weekly OHLC (e.g. HLCD). */
export function resampleOHLC(
  times: ReadonlyArray<number>,
  opens: ReadonlyArray<number>,
  highs: ReadonlyArray<number>,
  lows: ReadonlyArray<number>,
  closes: ReadonlyArray<number>,
  freq: HtFreq,
): { t: number[]; o: number[]; h: number[]; l: number[]; c: number[] } {
  const t: number[] = [], o: number[] = [], h: number[] = [], l: number[] = [], c: number[] = [];
  const n = closes.length;
  if (n === 0) return { t, o, h, l, c };
  const intraday = isIntradaySeries(times);
  let key = bucketKey(times[0]!, freq, intraday);
  let bo = opens[0]!, bh = highs[0]!, bl = lows[0]!, bc = closes[0]!, bt = times[0]!;
  const flush = () => { t.push(bt); o.push(bo); h.push(bh); l.push(bl); c.push(bc); };
  for (let i = 1; i < n; i++) {
    const k = bucketKey(times[i]!, freq, intraday);
    if (k === key) {
      if (highs[i]! > bh) bh = highs[i]!;
      if (lows[i]! < bl) bl = lows[i]!;
      bc = closes[i]!;
      bt = times[i]!;
    } else {
      flush();
      key = k;
      bo = opens[i]!; bh = highs[i]!; bl = lows[i]!; bc = closes[i]!; bt = times[i]!;
    }
  }
  flush(); // trailing (in-progress) bucket; the strict-before filter excludes it
  return { t, o, h, l, c };
}

/** Sum daily volumes into ISO-week / calendar-month buckets (in-progress bucket
 *  included), aligned with `resampleOHLC` so the two share a bucket count. */
export function resampleVolumes(
  times: ReadonlyArray<number>,
  volumes: ReadonlyArray<number>,
  freq: HtFreq,
): number[] {
  const out: number[] = [];
  const n = Math.min(times.length, volumes.length);
  if (n === 0) return out;
  const intraday = isIntradaySeries(times);
  let key = bucketKey(times[0]!, freq, intraday);
  let sum = Number.isFinite(volumes[0]!) ? volumes[0]! : 0;
  for (let i = 1; i < n; i++) {
    const k = bucketKey(times[i]!, freq, intraday);
    const v = Number.isFinite(volumes[i]!) ? volumes[i]! : 0;
    if (k === key) sum += v;
    else { out.push(sum); key = k; sum = v; }
  }
  out.push(sum);
  return out;
}

/**
 * For each daily bar, the index of the higher-timeframe bar that has CLOSED as
 * of that bar — Pine's `lookahead_off`. -1 until one exists. `dailyTimes` and
 * `htTimes` must both be ascending.
 *
 * ## The comparison is `<=`, and that is the whole point
 *
 * Buckets are stamped with their LAST bar's time (`resampleByLastClose`), so a
 * week is stamped with its Friday. A weekly bar closes at the end of Friday —
 * the same instant Friday's daily bar closes — so on Friday, `lookahead_off`
 * already returns THAT week. TradingView does not make you wait for Monday.
 *
 * This used to compare `<`, which excluded the week closing on the very bar
 * being resolved and put every higher-timeframe study one session behind
 * TradingView at every period boundary. Nothing in the test suite could see it:
 * `pine:check` diffs a study against a `.pine` port executed by this repo's own
 * Pine interpreter, and that interpreter resolves `request.security` through
 * THIS function — both sides carried the same offset and agreed perfectly with
 * each other while disagreeing with TradingView. It took a user holding the two
 * charts side by side (SPY, Fri 2026-07-17: TradingView bearish on the week
 * closing that day, the app still bullish until Monday). Hence the explicit
 * fixture in `scripts/checks/pine.ts` that asserts the Friday value by hand,
 * rather than by comparing us to ourselves.
 *
 * ## The last bar
 *
 * The trailing in-progress bucket is stamped with the newest bar's time, so on
 * the final bar `<=` selects it — which is exactly Pine's realtime edge (the
 * forming period). Callers need no special case for it; the ones that used to
 * force `idx[n-1]` by hand were compensating for the `<` above.
 */
export function lastClosedHtIndexPerDay(
  dailyTimes: ReadonlyArray<number>,
  htTimes: ReadonlyArray<number>,
): number[] {
  const out: number[] = new Array(dailyTimes.length).fill(-1);
  let cursor = -1;
  for (let i = 0; i < dailyTimes.length; i++) {
    const dt = dailyTimes[i]!;
    while (cursor + 1 < htTimes.length && htTimes[cursor + 1]! <= dt) cursor++;
    out[i] = cursor;
  }
  return out;
}

/** For each daily bar, the index of the higher-timeframe bucket it BELONGS to
 *  (inclusive of the in-progress bucket) — the smallest `k` with `htTimes[k] ≥
 *  daily t`. Unlike `lastClosedHtIndexPerDay`, the latest day maps to the
 *  current (in-progress) week, so a weekly "bars-since" age counts the current
 *  weekly bar. For latest-value (scanner) use; not lookahead-off for history. */
export function htIndexPerDay(
  dailyTimes: ReadonlyArray<number>,
  htTimes: ReadonlyArray<number>,
): number[] {
  const out: number[] = new Array(dailyTimes.length).fill(-1);
  let cursor = 0;
  for (let i = 0; i < dailyTimes.length; i++) {
    const dt = dailyTimes[i]!;
    while (cursor < htTimes.length && htTimes[cursor]! < dt) cursor++;
    out[i] = cursor < htTimes.length ? cursor : htTimes.length - 1;
  }
  return out;
}

/** The higher-timeframe OHLCV series VISIBLE AS OF one daily bar: every
 *  completed bucket before the bar's bucket, plus the bar's own in-progress
 *  bucket aggregated through that bar (open = first, high/low = running
 *  extremes, close = the bar's close, volume = running sum). `cmp` carries an
 *  aligned comparative (e.g. SPY) close per bucket when supplied. The LAST
 *  element is always the in-progress bucket as of "now". */
export type HtfAsOf = {
  o: number[];
  h: number[];
  l: number[];
  c: number[];
  v: number[];
  cmp?: number[];
};

/**
 * Walk-forward HTF projection — THE NO-REPAINT RULE for every weekly/monthly
 * indicator rendered across daily bars.
 *
 * For each daily bar i it builds the HTF series VISIBLE AS OF bar i (completed
 * buckets + i's in-progress bucket through i's running values) and calls
 * `evalLast` on it; the returned scalar is bar i's value. Because the series is
 * truncated at i, a past bar's value can NEVER change when later bars arrive —
 * what you see on a historical bar is exactly what it showed when it was the
 * live bar. The latest bar gets the full series (i.e. the live current period),
 * so it equals the scanner's single latest value by construction.
 *
 * `evalLast` must be CAUSAL — it returns the value of the LAST (in-progress)
 * bucket given the whole as-of series (latches / bars-since counts / recursive
 * RSI-EMA are all fine: they're recomputed on the truncated series each bar, so
 * the completed prefix is deterministic and only the in-progress tail varies).
 *
 * Cost is O(bars × buckets) — the as-of arrays are mutated in place (the last
 * element extends while a bucket forms, a fresh element is pushed when it
 * rolls), so there is no per-bar reallocation, only the evalLast recompute.
 * Fine for chart ranges; the scanner never calls this (it needs one value, not
 * a series).
 */
export function htfWalkForward<T>(
  times: ReadonlyArray<number>,
  opens: ReadonlyArray<number>,
  highs: ReadonlyArray<number>,
  lows: ReadonlyArray<number>,
  closes: ReadonlyArray<number>,
  volumes: ReadonlyArray<number>,
  freq: HtFreq,
  evalLast: (s: HtfAsOf) => T | null,
  opts: { compareCloses?: ReadonlyArray<number> } = {},
): (T | null)[] {
  const n = closes.length;
  const out: (T | null)[] = new Array(n).fill(null);
  if (n === 0 || times.length !== n) return out;
  const cmpIn = opts.compareCloses && opts.compareCloses.length === n ? opts.compareCloses : null;

  const s: HtfAsOf = { o: [], h: [], l: [], c: [], v: [], ...(cmpIn ? { cmp: [] } : {}) };
  const intraday = isIntradaySeries(times);
  let curKey: string | null = null;
  for (let i = 0; i < n; i++) {
    const key = bucketKey(times[i]!, freq, intraday);
    if (key !== curKey) {
      // A new bucket starts — freeze the prior one (already in the arrays) and
      // push a fresh in-progress element seeded from this bar.
      s.o.push(opens[i]!);
      s.h.push(highs[i]!);
      s.l.push(lows[i]!);
      s.c.push(closes[i]!);
      s.v.push(volumes[i]!);
      if (s.cmp && cmpIn) s.cmp.push(cmpIn[i]!);
      curKey = key;
    } else {
      // Extend the in-progress (last) bucket through this bar.
      const j = s.c.length - 1;
      if (highs[i]! > s.h[j]!) s.h[j] = highs[i]!;
      if (lows[i]! < s.l[j]!) s.l[j] = lows[i]!;
      s.c[j] = closes[i]!;
      s.v[j]! += volumes[i]!;
      if (s.cmp && cmpIn) s.cmp[j] = cmpIn[i]!;
    }
    out[i] = evalLast(s);
  }
  return out;
}
